export const CONSENT_COOKIE_NAME = "arpano_cookie_consent";
export const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

export interface CookieConsent {
  necessary: boolean; // Always true, required for site functionality
  analytics: boolean; // Google Analytics
  marketing: boolean; // Google Ads, Meta Pixel, LinkedIn Insight
  timestamp: string; // ISO date when consent was given
}

export const defaultConsent: CookieConsent = {
  necessary: true,
  analytics: true,
  marketing: true,
  timestamp: "",
};

export function parseConsentCookie(
  cookieValue: string | undefined,
): CookieConsent | null {
  if (!cookieValue) return null;
  try {
    const parsed = JSON.parse(cookieValue);
    if (
      typeof parsed.necessary === "boolean" &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.marketing === "boolean" &&
      typeof parsed.timestamp === "string"
    ) {
      return parsed as CookieConsent;
    }
    return null;
  } catch {
    return null;
  }
}

export function serializeConsent(consent: CookieConsent): string {
  return JSON.stringify(consent);
}

export async function setConsentCookie(consent: CookieConsent): Promise<void> {
  const value = serializeConsent(consent);
  if ("cookieStore" in window) {
    const expiresTimestamp = Date.now() + CONSENT_COOKIE_MAX_AGE * 1000;
    await window.cookieStore.set({
      name: CONSENT_COOKIE_NAME,
      value: encodeURIComponent(value),
      path: "/",
      expires: expiresTimestamp,
      sameSite: "lax",
    });
  } else {
    // biome-ignore lint/suspicious/noDocumentCookie: Fallback for browsers without Cookie Store API
    document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax`;
  }
}

export function getConsentFromCookie(): CookieConsent | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split("=");
    if (name === CONSENT_COOKIE_NAME) {
      const value = decodeURIComponent(valueParts.join("="));
      return parseConsentCookie(value);
    }
  }
  return null;
}
